import React, {useEffect, useState, useRef} from 'react';
import {SenderReportProps} from './SenderReport.props';
import './SenderReport.styles.css';
import {PageHeader} from "../../components/organisms/PageHeader";
import { Box, Fade, IconButton, Stack, Tooltip } from "@mui/material";
import ChangeWithZone from "../../hoc/ChangeWithZone";
import moment from "moment-timezone";
import {
    getTimeZoneName,
    noUserDataRedirect,
    handleDisplayForLargeNumber,
    handleMonthName,
    autoDownload
} from "../../utils/common";
import {StackedColumnChartReport} from "../../components/molecules/StackedColumnChartReport";
import {DataTable} from "../../components/molecules/DataTable";
import {GridColDef} from "@mui/x-data-grid";
import {Pagination} from "../../components/atoms/Pagination";
import {STATUS_CODE, REPORT_CHART_NA_DATA, REPORT_MENU_TYPE, OUTPUT_FORMAT} from "../../constants/contants";
import {getReports} from "../../api/emailTriage";
import {DropDownFilter} from "../../components/molecules/DropDownFilter";
import Button from "@mui/material/Button";
import {SearchFilter} from "../../components/molecules/SearchFilter";
import {Images} from "../../configs/images";
import Skeleton from "@mui/material/Skeleton";
import { DateRangePicker } from "../../components/atoms/DateRangePicker";
import DownloadIcon from "@mui/icons-material/Download";
import {LoadingPage} from "../../components/organisms/LoadingPage";

const SenderReportFull: React.FC<SenderReportProps> = () => {
    const tz_name = getTimeZoneName()
    const defaultDateRange: any = {
        startDate: moment().startOf('month').toDate(),
        endDate: moment().endOf('month').toDate()
    }

    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [startPoint, setStartPoint] = useState(0);
    const [endPoint, setEndPoint] = useState(0);
    const [maxPage, setMaxPage] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0)
    const [isTableLoading, setIsTableLoading] = useState(true);
    const [loadingPage, setLoadingPage] = useState(false);
    const [senderDataChart, setSenderDataChart] = useState<any>({});
    const [senderDataTable, setSenderDataTable] = useState<any>([]);
    const [sortBy, setSortBy] = useState<any>('total_messages');
    const [sortDirection, setSortDirection] = useState<any>('desc');
    const [dateRange, setDateRange] = useState({
        startDate: defaultDateRange.startDate,
        endDate: defaultDateRange.endDate,
    })
    const initialFilter = {
        filterData: {
            searchText: '',
            status: [],
            startDate: moment.utc(dateRange.startDate).format('YYYY-MM-DD HH:mm:ss'),
            endDate: moment.utc(dateRange.endDate).format('YYYY-MM-DD HH:mm:ss'),
        }
    };
    const [senderFilter, setSenderFilter] = useState<any>(initialFilter)
    const [isFilterOnly, setIsFilterOnly ] = useState<any>(false)
    const [timer, setTimer] = useState<any>(null);
    const [selectedItemsDropDown, setSelectedItemsDropDown] = useState<any>({});
    const [filterSearchKeyword, setFilterSearchKeyword] = useState('');
    const [isOpenAttribute, setIsOpenAttribute] = useState<any>({status: null})
    const senderReportRef = useRef<any>(null)

    //handle for report table responsive by screen breakpoints
    const defaultTableColumnWidth = {
        report_value: 350,
        total_messages: 340,
        clean: 340,
        undetermined: 340,
        threat: 170
    }
    const [tableColumnWidth, setTableColumnWidth] = useState<any>(defaultTableColumnWidth)

    const statusFilter:any = [
        {id: 1, content: 'Clean'},
        {id: 2, content: 'Undetermined'},
        {id: 3, content: 'Threat'}
    ]

    const dropDownFilterArr:any = [
        {
            label: 'Status',
            type: 'status',
            listArr: statusFilter
        }
    ]

    const onSort = (model: any) => {
        model.length > 0 ? setSortDirection(model[0].sort) : setSortDirection('desc')
        model.length > 0 ? setSortBy(model[0].field) : setSortBy('total_messages')
    };

    const getSenderRecords = (params: any) => {
        setIsTableLoading(true)

        params["page"] = page
        params["per_page"] = perPage
        params["report_type"] = REPORT_MENU_TYPE.SENDER
        params["sort_dir"] = sortDirection
        params["sort_by"] = sortBy

        console.log("chart param: ", params)

        // call data for report chart
        if (!isFilterOnly){
            params["output_format"] = OUTPUT_FORMAT.GRAPH
            getReports(params).then(res =>{
                if(res && res.status === STATUS_CODE.OK){
                    const chartDataReceived:any = res.data
                    setSenderDataChart(chartDataReceived || REPORT_CHART_NA_DATA);
                    console.log("chart data received: ", chartDataReceived)
                    setIsTableLoading(false);
                }
            }).catch((e) => {
                console.log(e)
            })
        }

        // call data for report table
        params["output_format"] = OUTPUT_FORMAT.TABLE
        getReports(params).then(res =>{
            if(res && res.status === STATUS_CODE.OK){
                const tableDataReceived:any = res.data

                setSenderDataTable(tableDataReceived.list || []);
                setTotalRecords(tableDataReceived.total_items);
                setStartPoint(tableDataReceived.start_items_per_page);
                setEndPoint(tableDataReceived.end_items_per_page)
                setMaxPage(tableDataReceived.max_page);
                setIsTableLoading(false);
            }
        }).catch((e) => {
            console.log(e)
        })
    };

    const loadSenderPage = () => {
        const userData = localStorage.getItem('userData');
        if (userData === null) {
            noUserDataRedirect()
        } else {
            getSenderRecords(senderFilter);
        }
    }

    useEffect(() => {
        loadSenderPage()
    }, [senderFilter, page, perPage, isFilterOnly, sortDirection])

    const handleRoundUpNumber = (roundedNumber: number) => { return Math.ceil(roundedNumber) }

    const handleResize = () => {
        if (senderReportRef.current){
            const screenWidth = window.screen.width
            let tableWidth = document?.getElementById('root')?.classList.value.includes('menu-opened') ? (screenWidth - 32) : (screenWidth - 257)

            let tempColumnWidth = {
                report_value: handleRoundUpNumber(tableWidth * 0.2),
                total_messages: handleRoundUpNumber(tableWidth * 0.2),
                clean: handleRoundUpNumber(tableWidth * 0.16),
                undetermined: handleRoundUpNumber(tableWidth * 0.2),
                threat: handleRoundUpNumber(tableWidth * 0.16)
            }

            if (screenWidth <= 900){
                tempColumnWidth = {
                    ...tempColumnWidth,
                    total_messages: handleRoundUpNumber(tableWidth * 0.25),
                    clean: handleRoundUpNumber(tableWidth * 0.2),
                    threat: handleRoundUpNumber(tableWidth * 0.2)
                }
            }

            setTableColumnWidth(tempColumnWidth)
        }
    }

    useEffect(() => {
        handleResize()

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [])

    const columns: GridColDef[] = [
        {
            field: 'report_value', headerName: 'Sender Domain', width: tableColumnWidth.report_value, sortable: true,
            renderCell: (row) => {
                return <Tooltip placement={"top"}
                                title={`${row.formattedValue}`}
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            maxWidth: "450px",
                                        },
                                    },
                                }}
                >
                    <span className={'MuiDataGrid-cellContent'}>{`${row.formattedValue}`}</span>
                </Tooltip>
            }
        },
        {
            field: 'total_messages', headerName: 'Email Count', width: tableColumnWidth.total_messages, sortable: true,
            renderCell: (row) => {
                return handleDisplayForLargeNumber(row.formattedValue)
            }
        },
        {
            field: 'clean', headerName: 'Clean', width: tableColumnWidth.clean, sortable: true,
            renderCell: (row) => {
                return handleDisplayForLargeNumber(row.formattedValue)
            }
        },
        {
            field: 'undetermined', headerName: 'Undetermined', width: tableColumnWidth.undetermined, sortable: true,
            renderCell: (row) => {
                return handleDisplayForLargeNumber(row.formattedValue)
            }
        },
        {
            field: 'threat', headerName: 'Threat', width: tableColumnWidth.threat, sortable: true,
            renderCell: (row) => {
                return handleDisplayForLargeNumber(row.formattedValue)
            }
        },
    ];

    const handlePerRowsChange = (e: any) => {
        const value = parseInt(e.target.value)
        setPage(1)
        setPerPage(value)
        setIsFilterOnly(true)
    }

    const handlePageChange = (pageChanged: number) => {
        setPage(pageChanged)
        setIsFilterOnly(true)
    }

    const paginationComponent = () => {
        return <Pagination
            itemsPerPage = {perPage}
            currentPage = {page}
            startPoint = {startPoint}
            endPoint = {endPoint}
            maxPage = {maxPage}
            totalItems = {totalRecords}
            handlePerRowsChange={handlePerRowsChange}
            handlePageChange={handlePageChange}
        />
    }

    useEffect(() => {
        const {startDate, endDate} = dateRange;
        setSenderFilter((prev:any) => {
            return {
                ...prev,
                filterData: {
                    ...prev.filterData,
                    startDate: startDate,
                    endDate: endDate,
                }
            }
        });
        if (Object.keys(senderDataChart).length > 0) {
            setSenderDataChart({})
        }
        setIsFilterOnly(false);
    }, [dateRange]);

    const handleApplyDateRange = (picker: any) => {
        const { startDate, endDate } = picker;
        setDateRange({
            startDate: startDate,
            endDate: endDate,
        });
    };

    const handleSetFilterDropDown = () => {
        // Handle when changing selected item in filter dropdowns
        handleSetFilter()
    }

    useEffect(() => {
        handleSetFilterDropDown()
    }, [selectedItemsDropDown])

    const handleDropDownSelected = (item: any, type: any) => {
        setSelectedItemsDropDown((prev: any) => {
            return ({
                ...prev,
                [type]: item,
            });
        });
    };

    const handleClearDropDownSelected = (type: any) => {
        setSelectedItemsDropDown((prev:any) => {
            return ({
                ...prev,
                [type]: [],
            })
        });
    };

    const handleResetFilter = () => {
        setIsFilterOnly(true)
        setPage(1)
        setSenderFilter((prev:any) => {
            return {
                ...prev,
                filterData: {
                    ...prev.filterData,
                    searchText: '',
                    status: []
                }
            }
        })
        setFilterSearchKeyword('')
        setSelectedItemsDropDown({})
    }

    const handleSetFilter = (event: any = null) => {
        setPage(1)
        setIsFilterOnly(true)
        setSenderFilter((prev:any) => {
            // the event is null when acting in dropdown filter
            // The event is not null when the Enter key is pressed in the search input.
            let search_text = prev.filterData.searchText
            let status_arr = prev.filterData.status

            if (event) {
                search_text = event.target.value.trim()
            }else {
                status_arr = selectedItemsDropDown.status ? selectedItemsDropDown.status : status_arr
            }

            if (search_text !== '' || status_arr.length > 0) {
                const {startDate, endDate} = dateRange;
                return ({
                    ...prev,
                    filterData: {
                        ...prev.filterData,
                        status: status_arr,
                        searchText: search_text,
                        startDate: startDate,
                        endDate: endDate,
                    }
                })
            } else {
                return initialFilter
            }
        });
    }

    const handleEnterSearchInput = (event:any) => {
        // Handle when Enter on the search input
        if (timer) {
            clearTimeout(timer)
            setTimer(null)
        }
        return new Promise((resolve) => {
            const timers = setTimeout(async () => {
                if (event.keyCode === 13) {
                    handleSetFilter(event)
                }
            }, 300);
            setTimer(timers);
        });
    }

    const chartOptions = {
        colors : ['#BDE541', '#9FD8DB', '#0E4F83'],
        fill: {
            colors: ['#BDE541', '#9FD8DB', '#0E4F83'],
            opacity: 1,
        },
        chart: {
            toolbar: {
                show: false,
            },
            stacked: true,
            stackType: '100%'
        },
        plotOptions: {
            bar: {
                columnWidth: '60%',
                horizontal: true
            }
        },
        tooltip: {
            enabled: false,
            y: {
                formatter: function (val:any) {
                    return val
                }
            }
        },
        dataLabels: {
            enabled: false
        },
        legend: {
            show: true,
            position: 'top',
            offsetX: 40,
            fontSize: '11px',
            fontFamily: 'Montserrat, Helvetica, Arial',
            fontWeight: 400,
            color: '#808888',
            markers: {
                width: 14,
                height: 14,
                radius: 14,
            } ,
            onItemClick: {
                toggleDataSeries: false
            },
        },
        grid: {
            show: false
        },
        xaxis: {
            position: "top",
            axisTicks: {
                show: false,
            },
            axisBorder: {
                show: false,
            },
            labels: {
                style: {
                    colors: ['#808888'],
                    fontSize: '10px',
                    fontFamily: 'Montserrat, Helvetica, Arial, sans-serif',
                    fontWeight: 400
                },
                formatter: (value: any) => {  return (parseInt(value)/10)%2 === 0 ? value + '%': '' },
            },
            categories: Object.keys(senderDataChart).length > 0 ? senderDataChart["category_list"] : []
        },
        yaxis: {
            labels: {
                show: true,
                align: 'right',
                style: {
                    colors: ['#808888', '#808888','#808888', '#808888','#808888', '#808888','#808888', '#808888','#808888', '#808888'],
                    fontSize: '10px',
                    fontFamily: 'Montserrat, Helvetica, Arial, sans-serif',
                    fontWeight: 400,
                    textTransform: "capitalize"
                },
                offsetX: 0,
            }
        },
    };

    const handleDownload = () => {
        setLoadingPage(true)
        const params = {
            ...senderFilter,
            "page": page,
            "per_page": perPage,
            "report_type": REPORT_MENU_TYPE.SENDER,
            "sort_dir": sortDirection,
            "sort_by": sortBy,
            "is_down_load_report": true
        };

        getReports(params).then(res => {
            const { download_url } = res?.data
            if (download_url) {
                autoDownload(download_url)
            }
            setLoadingPage(false)
        }).catch((e) => {
            console.log("download sender report failed: ", e)
            setLoadingPage(false)
        })
    }

  return (
      <Box className='sender-report-page page-content-wrapper'>
          <PageHeader
              pageTitle={"Sender Report"}
              children={
                  <DateRangePicker
                    start={dateRange.startDate}
                    end={dateRange.endDate}
                    fontSize={'14px'}
                    color={'#011111'}
                    colorText={'#011111'}
                    width={'165px'}
                    height={'36p'}
                    cssClass={'daterange-input'}
                    dates={dateRange}
                    handleApplyDateRange={handleApplyDateRange}
                    titleCalendar="DATE:"
                    maxSpan={{'years': 2}}
                  />
              }
          />

        <Box className={"sender-report-content"} ref={senderReportRef}>
            <Box className={"chart-box"}>
                <Box style={{color: "#808888"}}>
                    <span style={{fontSize: "20px"}}>Top Sender Domain</span>
                    <span style={{display: "block", fontSize: "12px", marginTop: "5px"}}>Breakdown of Sender Accounts | {`${moment.utc(dateRange.startDate).tz(tz_name).format('DD/MM/YYYY')} - ${moment.utc(dateRange.endDate).tz(tz_name).format('DD/MM/YYYY')}`}</span>
                </Box>

                <Box>
                    {
                         Object.keys(senderDataChart).length === 0 ? <Skeleton variant="rectangular" sx={{width: '100%', height: '200px', marginTop: '25px', borderRadius: '4px'}} /> :
                            <StackedColumnChartReport
                                data={senderDataChart}
                                chartOptions={chartOptions}
                                layoutHeight = {senderDataChart["category_list"]?.length > 0 ? `${senderDataChart["category_list"].length * 30 + 120 + (senderDataChart["category_list"].length - 1) * 20}px` : "300px"}
                            />
                    }

                    {/*<ColumnChart*/}
                    {/*    dataList={[78, 64, 51, 39, 34, 22, 16, 5, 3, 2]}*/}
                    {/*    categoryList={['.Jpeg', '.Pdf', '.Txt', '.Eml', '.Png', '.Msg', '.Html', '.Bin', '.Rtf', '.Vcf']}*/}
                    {/*    isHorizontal={true}*/}
                    {/*    labelOffSetX={20}*/}
                    {/*    borderRadiusCorner={"end"}*/}
                    {/*/>*/}
                </Box>
            </Box>

            <Box className={"table-box"}>
                <Box className={'sender-search-bar'}>
                    <SearchFilter
                        height={38}
                        margin={'0'}
                        widthTextInput={180}
                        titleInput={'Search'}
                        iconSrc={Images.SearchIcon}
                        borderRadius={'4px'}
                        handleKeyDownEvent={handleEnterSearchInput}
                        inputValue={filterSearchKeyword}
                        setInputValue={setFilterSearchKeyword}
                    />
                    <Box className={'sender-search-dropdown'}>
                        {
                            dropDownFilterArr ? dropDownFilterArr.map((item: any, index:number) =>
                                <DropDownFilter isOpen={isOpenAttribute}
                                                setIsOpen={setIsOpenAttribute}
                                                clearFilerSelection={handleClearDropDownSelected}
                                                onSelectItem={handleDropDownSelected}
                                                selectedItems={selectedItemsDropDown}
                                                label={item.label}
                                                type={item.type}
                                                arrList={item.listArr}
                                                width={90}
                                                key={index}
                                />
                            ) : null
                        }
                        <Button
                            data-testid={"resetSearchFilterBtn"} className={"sender-reset-search-btn"} onClick={() => handleResetFilter()} variant="text" size={"large"}
                            sx={{
                                padding: "8px",
                                width: 'fit-content',
                                height: '40px',
                                lineHeight: '16px',
                                fontFamily: 'Metropolis',
                                fontSize: '16px',
                                fontWeight: 600,
                                backgroundColor: 'transparent',
                                color: '#345C8B'
                            }}
                        >Reset</Button>
                    </Box>
                </Box>
                {
                    senderDataTable.length > 0 ? <Stack direction="row" justifyContent="flex-end" marginBottom={2.5}>
                        <Tooltip title="Download">
                            <IconButton id="download-button" data-testid="download-button-testid" onClick={handleDownload}><DownloadIcon/></IconButton>
                        </Tooltip>
                    </Stack> : <></>
                }
                <DataTable columns={columns}
                           data={senderDataTable}
                           pagination={true}
                           CustomPagination = {paginationComponent}
                           loadingTable={isTableLoading}
                           onSortModelChange={onSort}
                           initialSortCol={'total_messages'}
                />
            </Box>
        </Box>
        <LoadingPage open={loadingPage}/>
      </Box>
  );
};

export const SenderReport = ChangeWithZone(SenderReportFull);
